import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Meta, Title } from "@angular/platform-browser";
import { SeoServiceService } from "src/app/services/seo-service.service";

interface ProductBundle {
    icon: string;
    title: string;
}

interface SolutionData {
    type: string;
    icon: string;
    title: string;
    problem: string;
    solution: string;
    bundles: ProductBundle[];
    ctaText: string;
    ctaLink: string;
    heroImage?: string;
}

@Component({
    selector: "app-solution-detail",
    templateUrl: "./solution-detail.component.html",
    styleUrls: ["./solution-detail.component.scss"],
})
export class SolutionDetailComponent implements OnInit {
    solutionData: SolutionData | null = null;

    private solutionsConfig: { [key: string]: SolutionData } = {
        schools: {
            type: 'schools',
            icon: 'school',
            title: 'Schools',
            problem: 'Traditional classrooms lack engagement, standardisation & digital tools needed for modern learning.',
            solution: 'Virsa provides a complete smart classroom ecosystem with Interactive Flat Panels, IFP Protection (Shutter), LMS, K-12 Content, Digital E-Library, and teacher tools.',
            bundles: [
                { icon: 'tv', title: 'Interactive Flat Panel (55/65/75/86/98/105/110)' },
                { icon: 'dashboard', title: 'IFP Protection (Shutter)' },
                { icon: 'dashboard', title: 'LMS Platform (Teacher + Student + Admin modules)' },
                { icon: 'library_books', title: 'K-12 Digital Content' },
                { icon: 'library_books', title: 'Digital E-Library' },
                { icon: 'computer', title: 'All-in-One PCs' }
            ],
            ctaText: 'Schedule a Demo for School Upgrade',
            ctaLink: '/form/contactUs',
            heroImage: 'https://d3uop73ba1wtqg.cloudfront.net/uploads/F1VP8OCG7N5DHwgI8QAPc8z6pyvCm1sEHE9roG1o.jpg'
        },
        'education-institutions': {
            type: 'education-institutions',
            icon: 'book_2',
            title: 'Education Institutions (Coaching)',
            problem: 'Coaching classes need better engagement, hybrid teaching tools & structured test systems.',
            solution: 'Interactive Flat Panels, IFP Protection (Shutters), All In One PCs,  LMS, and K-12 Content designed for education institutions (coaching).',
            bundles: [
                { icon: 'tv', title: 'Interactive Flat Panel' },
                { icon: 'dashboard', title: 'IFP Protection (Shutter)' },
                { icon: 'computer', title: 'All-in-One PCs' },
                { icon: 'assignment', title: 'LMS (Assessments, Attendance, Test Series)' },
                { icon: 'menu_book', title: 'Subject Content' },
                { icon: 'videocam', title: 'Recording & Live Class Setup' },

            ],
            ctaText: 'Schedule a Demo for Education Institution (Coaching)',
            ctaLink: '/form/contactUs',
            heroImage: 'https://d3uop73ba1wtqg.cloudfront.net/uploads/V40vfhTRVGIJETj6atJiCStSw8lHg3Hd4tnKyPCn.jpg'
        },
        corporates: {
            type: 'corporates',
            icon: 'business',
            title: 'Corporates',
            problem: 'Corporate teams struggle with training sessions, meetings, conference presentations, and effective boardroom tools.',
            solution: 'Smart conferencing panels, presentation solutions, and advanced boardroom tools designed for modern corporate environments.',
            bundles: [
                { icon: 'tv', title: 'Interactive Flat Panel (Meeting Mode)' },
                { icon: 'computer', title: 'All-in-One PCs' },
                { icon: 'videocam', title: 'Video Conferencing Setup' }
            ],
            ctaText: 'Request Corporate Solution',
            ctaLink: '/form/contactUs',
            heroImage: 'https://d3uop73ba1wtqg.cloudfront.net/uploads/nHOvX7YpBZAG4uj00BPuQq7H2gvOVisK1TzDWZ70.jpg'
        },
        government: {
            type: 'government',
            icon: 'account_balance',
            title: 'Government',
            problem: 'Government institutions require scalable, reliable, tender-compliant digital solutions.',
            solution: 'PAN-India procurement-ready panels, ICT labs, LMS & skill development platforms.',
            bundles: [
                { icon: 'tv', title: 'Interactive Flat Panels (Govt Series)' },
                { icon: 'computer', title: 'ICT Lab Setup' },
                { icon: 'school', title: 'LMS + Digital Content' },
                { icon: 'desktop_windows', title: 'All-in-One PCs with Shutter' },
                { icon: 'computer', title: 'All-in-One PCs' }
            ],
            ctaText: 'Download Govt Capability Profile',
            ctaLink: '/form/contactUs',
            heroImage: 'https://d3uop73ba1wtqg.cloudfront.net/uploads/MO5HIOhaMiuhKmV8qbiaYBgepxfM6pHhEisXV9sB.jpg'
        },
        partners: {
            type: 'partners',
            icon: 'handshake',
            title: 'Partners',
            problem: 'Partners need a high-demand, high-margin EdTech portfolio with OEM support.',
            solution: 'Partner with Virsa to sell panels, LMS, content & school solutions with strong backend support.',
            bundles: [
                { icon: 'inventory', title: 'Partnership Starter Kit' },
                { icon: 'description', title: 'Product Catalogue' },
                { icon: 'model_training', title: 'Training & Sales Tools' },
                { icon: 'campaign', title: 'Marketing Support' },
                { icon: 'computer', title: 'All-in-One PCs' }
            ],
            ctaText: 'Become a Partner',
            ctaLink: '/partner',
            heroImage: 'https://d3uop73ba1wtqg.cloudfront.net/uploads/LiAdf9V4SvRbiZRtI7ioolbpobStB9Rn886cdObO.jpg'
        },
        coaching: {
            type: 'coaching',
            icon: 'psychology',
            title: 'Coaching Centres',
            problem: 'Coaching classes need better engagement, hybrid teaching tools & structured test systems.',
            solution: 'Smart boards + LMS + content designed for coaching teaching styles.',
            bundles: [
                { icon: 'tv', title: 'Interactive Flat Panel' },
                { icon: 'assignment', title: 'LMS (Assessments, Attendance, Test Series)' },
                { icon: 'menu_book', title: 'Subject Content' },
                { icon: 'videocam', title: 'Recording & Live Class Setup' },
                { icon: 'computer', title: 'All-in-One PCs' }
            ],
            ctaText: 'Upgrade Your Coaching Centre',
            ctaLink: '/form/contactUs'
        },
        'students-parents': {
            type: 'students-parents',
            icon: 'family_restroom',
            title: 'Students & Parents',
            problem: 'Parents look for structured, high-quality K-12 learning & cultural education at home.',
            solution: 'Digital content + culture classes accessible on any device.',
            bundles: [
                { icon: 'library_books', title: 'K-12 Digital Content' },
                { icon: 'phone_android', title: 'Student LMS App' },
                { icon: 'computer', title: 'All-in-One PCs' }
            ],
            ctaText: 'Start Learning at Home',
            ctaLink: '/k-12-digital-content'
        }
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private meta: Meta,
        private titleService: Title,
        private seoService: SeoServiceService
    ) { }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            const solutionType = params['type'];
            this.solutionData = this.solutionsConfig[solutionType];

            if (!this.solutionData) {
                // Redirect to 404 if solution type not found
                this.router.navigate(['/404']);
                return;
            }

            // Set page title and meta tags
            this.titleService.setTitle(`${this.solutionData.title} Solution | Virsa`);
            this.seoService.createLinkForCanonicalURL();
            this.meta.updateTag({
                name: "description",
                content: this.solutionData.solution
            });
        });
    }
}
